import { useRef, useState } from "react";
import { api, API, getUser } from "../lib/api";
import { Upload, X, Loader2, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";

/**
 * MultiImageUpload — up to `max` photos per post, uploaded to the same
 * `/upload` endpoint that `ImageUpload` uses. Value is a string[] of the
 * fully-qualified public URLs. `onChange` receives the new array.
 *
 * Three ways to add photos:
 *   1. Click the "Add photo" tile → native file picker (multi-select on).
 *   2. Drop files onto the tile.
 *   3. Ctrl/Cmd+V (paste) — pulls an image out of the clipboard (nice on
 *      desktop; harmless on mobile where clipboard rarely has an image).
 *
 * Each thumbnail has a small × in the corner to remove it. We keep the
 * upload strictly sequential so a `4G-on-mobile` user with 6 chunky
 * photos doesn't hammer the backend with concurrent multipart requests.
 */
export default function MultiImageUpload({ value = [], onChange, max = 6, testId = "multi-image" }) {
  const user = getUser();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const remaining = Math.max(0, max - value.length);

  const uploadOne = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(`/upload?user_id=${user.id}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return `${API}/files/${data.path}`;
  };

  const addFiles = async (fileList) => {
    if (!user || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter((f) => f && f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Please choose image files only");
      return;
    }
    const slots = remaining;
    if (slots === 0) {
      toast.error(`You can only add ${max} photos per post`);
      return;
    }
    const toUpload = files.slice(0, slots);
    if (files.length > slots) {
      toast.warning(`Only the first ${slots} of ${files.length} photos will be added (max ${max}).`);
    }
    setUploading(true);
    const added = [];
    try {
      for (const f of toUpload) {
        try {
          added.push(await uploadOne(f));
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Upload failed for ${f.name || "one photo"}`);
        }
      }
      if (added.length > 0) {
        onChange([...value, ...added]);
        toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} uploaded`);
      }
    } finally {
      setUploading(false);
    }
  };

  const onFileInput = (e) => {
    addFiles(e.target.files);
    // Reset so the same file can be re-picked after a remove.
    e.target.value = "";
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div
      data-testid={testId}
      onPaste={onPaste}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      tabIndex={0}
      className={`rounded-xl outline-none ${dragOver ? "ring-2 ring-[#4A6B56] ring-offset-2" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFileInput}
        className="hidden"
        data-testid={`${testId}-input`}
      />

      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div key={`${url}-${i}`} data-testid={`${testId}-item-${i}`} className="relative">
            <img
              src={url}
              alt={`Photo ${i + 1}`}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-[#E2DFD8]"
            />
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              aria-label={`Remove photo ${i + 1}`}
              onClick={() => removeAt(i)}
              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-[#E2DFD8] flex items-center justify-center hover:bg-red-50"
            >
              <X className="w-4 h-4" />
            </button>
            {i === 0 && value.length > 1 && (
              <span className="absolute bottom-1 left-1 text-[10px] bg-[#1B3624] text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                Main
              </span>
            )}
          </div>
        ))}

        {remaining > 0 && (
          <button
            type="button"
            data-testid={`${testId}-add`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl border-2 border-dashed border-[#E2DFD8] hover:border-[#4A6B56] flex flex-col items-center justify-center gap-1 text-[#6B6965] hover:text-[#1B3624] transition-colors"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span className="text-[11px] leading-tight text-center px-1">
              {uploading ? "Uploading…" : value.length === 0 ? "Add photos" : "Add another"}
            </span>
          </button>
        )}
      </div>

      <p className="mt-2 text-[11px] text-[#6B6965] flex items-center gap-1">
        <ClipboardPaste className="w-3 h-3" />
        Up to {max} photos · click to select multiple, drag & drop, or paste from clipboard
      </p>
    </div>
  );
}
